import React from 'react';
import { useForm } from 'react-hook-form';
import { DeleteGoalModal } from '../../components/DeleteGoalModal/DeleteGoalModal';
import { AddGoalModal } from '../../components/AddGoalModal/AddGoalModal';
import { GoalComponentProps } from '../../components/Goal/Goal.meta';
import {
  DebtManagementGoalData,
  UPSERT_GOAL_URL,
  FORM_ID,
  FormValues,
} from './DebtManagement.meta';
import { useGoalsQuery } from '../../api/useGoalsQuery';
import { useUpsertGoalMutation } from '../../api/useUpsertGoalMutation';
import { UpsertGoalModal } from '../../components/UpsertGoalModal/UpsertGoalModal';
import { Alert } from '../../../../CommonComponents/OPL/Alert/Alert';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { AssetBucketingSelect } from '../../components/AssetBucketingSelect/AssetBucketingSelect';
import { useGoalFormDisabled } from '../../hooks/useGoalFormDisabled';

interface Props extends GoalComponentProps {
  goalData: DebtManagementGoalData;
}

export const DebtManagement = (props: Props) => {
  const { clientId } = props;

  const { data } = useGoalsQuery({ clientId });

  if (!data) {
    return null;
  }

  const {
    assetBucketingOptions: { enableGoalAssetBucketing },
  } = data;

  if (!enableGoalAssetBucketing) {
    return <NonEditableGoal {...props} />;
  }

  return <EditableGoal {...props} />;
};

const NonEditableGoal = ({ id, clientId, name, onClose }: Props) => {
  if (id) {
    return (
      <DeleteGoalModal clientId={clientId} goalId={id} onClose={onClose} onSuccess={onClose} />
    );
  }

  return (
    <AddGoalModal
      clientId={clientId}
      name={name}
      message="After you click on Add Goal below, please add your existing liabilities within the Accounts section and view your Debt Management Analysis within the Goal Results section."
      url={UPSERT_GOAL_URL}
      onClose={onClose}
    />
  );
};

const EditableGoal = ({ clientId, onClose, assetBucketingIndices, ...rest }: Props) => {
  const readOnly = useGoalFormDisabled();

  const { control, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      assetBucketingIndices,
    },
    disabled: readOnly,
  });

  const {
    mutateAsync: upsertGoal,
    isLoading: isUpsertingGoal,
    error: upsertGoalError,
  } = useUpsertGoalMutation({ clientId, url: UPSERT_GOAL_URL });

  const onSubmit = async (values: FormValues) => {
    try {
      await upsertGoal(values);

      onClose();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <UpsertGoalModal
      clientId={clientId}
      formId={FORM_ID}
      isLoading={isUpsertingGoal}
      onClose={onClose}
      {...rest}
    >
      {upsertGoalError && <Alert message={upsertGoalError.message} variant="error" mb="16" />}

      <Text color="neutral.500" mb="16">
        Please add your existing liabilities within the Accounts section and view your Debt
        Management Analysis within the Goal Results section. You can optionally assign this goal to
        one or more asset buckets.
      </Text>

      <Box
        id={FORM_ID}
        as="form"
        onSubmit={handleSubmit(onSubmit)}
        aria-label="Debt Management goal form"
      >
        <AssetBucketingSelect clientId={clientId} control={control} />
      </Box>
    </UpsertGoalModal>
  );
};
